import { REWARD_RULE } from "@cede/types";
import { formatBalance } from "@cede/utils";
import { HoldCampaignWithStatus } from "@/types/holdCampaigns";

/**
 * Get epoch data with fallback logic: current epoch first, then first epoch
 */
export const getEpochWithFallback = <T>(
  campaign: HoldCampaignWithStatus,
  selector: (epoch: NonNullable<HoldCampaignWithStatus["epochs"]>[number] | undefined) => T | undefined,
): T | undefined => {
  if (!campaign.epochs || campaign.epochs.length === 0) {
    return undefined;
  }

  // Try current epoch first
  const currentEpoch = campaign.currentEpoch;
  if (currentEpoch) {
    const value = selector(currentEpoch);
    if (value !== undefined) {
      return value;
    }
  }

  // Fallback to first epoch
  const firstEpoch = campaign.epochs[0];
  return firstEpoch ? selector(firstEpoch) : undefined;
};

/**
 * Format campaign date range for display (matches trading campaign format)
 */
export const formatCampaignDateRange = (startAt: Date, endAt: Date): string => {
  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };
  return `${formatDate(startAt)} — ${formatDate(endAt)}`;
};

/**
 * Format APR percentage for display
 */
export const formatAPR = (apr: number): string => {
  return `${formatBalance(apr * 100, { roundForFiat: true, preventRoundingUp: true })}%`;
};

/**
 * Format token amount for display
 */
export const formatTokenAmount = (amount: number, symbol: string): string => {
  return `${formatBalance(amount.toString(), { roundForFiat: true })} ${symbol}`;
};

/**
 * Format participant count for display
 */
export const formatParticipantCount = (count: number): string => {
  return count.toLocaleString();
};

/**
 * Format minimum balance requirement
 */
export const formatMinimumBalance = (campaign: HoldCampaignWithStatus): string => {
  // Try to get from top-level field first, then fallback to epoch data
  const minimumBalance =
    campaign.minimumEligibleUsdBalance || getEpochWithFallback(campaign, (epoch) => epoch?.minimumEligibleUsdBalance);

  return `$${formatBalance(minimumBalance?.toString() || "0", { roundForFiat: true })}`;
};

/**
 * Format reward pool amount
 */
export const formatRewardPool = (campaign: HoldCampaignWithStatus): string => {
  // Try to get from top-level field first, then fallback to epoch data
  const rewardPoolAmount =
    campaign.rewardPoolAmount || getEpochWithFallback(campaign, (epoch) => epoch?.rewardPoolAmount);

  return formatTokenAmount(rewardPoolAmount || 0, campaign.rewardToken || "REWARD");
};

/**
 * Format number of spots for fixed spot campaigns
 */
export const formatNumberOfSpots = (numberOfSpots?: number): string => {
  return numberOfSpots?.toLocaleString() || "N/A";
};

/**
 * Get number of spots with fallback to first epoch
 */
export const getNumberOfSpots = (campaign: HoldCampaignWithStatus): number | undefined => {
  // Try to get from top-level field first, then fallback to epoch data
  return campaign.numberOfSpots || getEpochWithFallback(campaign, (epoch) => epoch?.numberOfSpots);
};

/**
 * Get epoch duration text with fallback to first epoch
 */
export const getEpochDurationText = (campaign: HoldCampaignWithStatus): string => {
  // Try to get from top-level field first, then calculate from first epoch
  let epochDuration = campaign.epochDuration;

  if (!epochDuration) {
    epochDuration = getEpochWithFallback(campaign, (epoch) => {
      const startDate = new Date(epoch?.startDate || new Date());
      const endDate = new Date(epoch?.endDate || new Date());
      const durationMs = endDate.getTime() - startDate.getTime();
      return Math.round(durationMs / (1000 * 60 * 60)); // Convert to hours
    });
  }

  if (!epochDuration) {
    return "N/A";
  }

  // Always display in hours
  return `${epochDuration} hours`;
};

export const getHoldRewardRule = (campaign: HoldCampaignWithStatus): REWARD_RULE =>
  campaign.rewardRule || getEpochWithFallback(campaign, (epoch) => epoch?.rewardRule) || REWARD_RULE.PROPORTIONAL;
